import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import api from "../../config/URL";
import { toast } from "react-toastify";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";
import { MultiSelect } from "react-multi-select-component";
import Select from "react-select";
import fetchOverAllStudentCompletionForm from "../List/StudentAllCompletedList";

function PaymentsAdd({ selectedCenter }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invoiceId = queryParams.get("invoiceId"); // Extract invoiceId from URL
  const student_Id = queryParams.get("student"); // Extract studentId from URL

  const navigate = useNavigate();
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [invoiceData, setInvoiceData] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [creditCardFee, setCreditCardFee] = useState(0);

  const invoiceOptions = invoiceData.map((invoice) => ({
    label: invoice.invoiceNumber,
    value: invoice.id,
    totalAmount: invoice.totalAmount,
  }));
  const [selectedInvoice, setSelectedInvoice] = useState([]);

  const validationSchema = Yup.object({
    studentId: Yup.string().required("Student Name is required"),
    invoiceIds: Yup.array()
      .of(Yup.string())
      .min(1, "Invoice is required")
      .required("Invoice is required"),
    paymentDate: Yup.string()
      .required("Payment Date is required")
      .test(
        "paymentDate-check",
        "Payment Date cannot be earlier than Invoice Date",
        function (value) {
          const { invoiceIds } = this.parent;
          if (invoiceIds.length > 0) {
            const earliestInvoice = invoiceData.reduce((earliest, invoice) => {
              const invoiceDate = new Date(invoice.invoiceDate);
              return invoiceDate < earliest ? invoiceDate : earliest;
            }, new Date());

            const paymentDate = new Date(value);
            if (paymentDate < earliestInvoice) {
              return this.createError({
                message: "Payment Date cannot be earlier than Invoice Date",
                path: "paymentDate",
              });
            }
          }
          return true;
        }
      ),

    paymentMethod: Yup.string().required("Payment Method is required"),

    bank: Yup.string()
      .nullable()
      .trim()
      .when("paymentMethod", {
        is: (val) => val === "Credit Card" || val === "Internet Banking",
        then: (schema) => schema.required("Bank Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    transactionNo: Yup.string()
      .nullable()
      .trim()
      .when("paymentMethod", {
        is: "Internet Banking",
        then: (schema) => schema.required("Transaction No is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    mobileNumber: Yup.string()
      .matches(/^\d{8}$/, "Mobile Number must be 8 digits")
      .nullable()
      .when("paymentMethod", {
        is: "Internet Banking",
        then: (schema) => schema.required("Mobile Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    file: Yup.mixed().notRequired(),
    remark: Yup.string().notRequired(),
  });

  // Formik Hook
  const formik = useFormik({
    initialValues: {
      centerId: selectedCenter,
      studentId: student_Id || "", // Set studentId from URL
      paymentDate: "",
      paymentMethod: "",
      paymentReference: "",
      paidAmount: "0",
      accountNo: "",
      bank: "",
      transactionNo: "",
      mobileNumber: "",
      file: null,
      remark: "",
      invoiceIds: [],
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      const formData = new FormData();
      formData.append("centerId", selectedCenter);
      formData.append("studentId", values.studentId);
      formData.append("invoiceIds", values.invoiceIds);
      formData.append("paymentDate", values.paymentDate);
      formData.append("paymentMethod", values.paymentMethod);
      formData.append("paymentReference", values.paymentReference);
      // Include credit card fee in paidAmount if payment method is Credit Card
      const finalPaidAmount =
        values.paymentMethod === "Credit Card"
          ? parseFloat(values.paidAmount) + creditCardFee
          : parseFloat(values.paidAmount);
      formData.append("paidAmount", finalPaidAmount.toFixed(2));
      formData.append("bank", values.bank);
      formData.append("transactionNo", values.transactionNo);
      formData.append("accountNo", creditCardFee);
      formData.append("mobileNumber", values.mobileNumber);
      formData.append("remark", values.remark);

      // Append file if exists
      if (values.file) {
        formData.append("file", values.file);
      }

      try {
        const response = await api.post("/createPayment", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.status === 201) {
          toast.success(response.data.message);
          navigate("/payment");
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to create payment"
        );
      } finally {
        setLoadIndicator(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  // Function to scroll to the first error field
  const scrollToError = (errors) => {
    const errorField = Object.keys(errors)[0]; // Get the first error field
    const errorElement = document.querySelector(`[name="${errorField}"]`); // Find the DOM element
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      errorElement.focus(); // Set focus to the error element
    }
  };

  // Watch for form submit and validation errors
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      scrollToError(formik.errors);
    }
  }, [formik.submitCount, formik.errors]);

  const fetchStudent = async (centerId) => {
    try {
      let student = [] || "";
      const numericCenterId = Number(centerId);
      if (numericCenterId === 0) {
        student = await fetchOverAllStudentCompletionForm();
      } else {
        student = await fetchAllStudentListByCenter(numericCenterId);
      }
      if (!Array.isArray(student)) {
        throw new Error("API did not return an array");
      }
      const formattedStudent = student.map((std) => ({
        value: std.id,
        label: std.studentNames,
      }));
      setStudentOptions(formattedStudent);
      setStudentData(student);

      // If student_Id is provided, pre-select the student
      if (student_Id) {
        const selectedStudent = formattedStudent.find(
          (option) => option.value === parseInt(student_Id)
        );
        if (selectedStudent) {
          formik.setFieldValue("studentId", selectedStudent.value);
          handleStudentChange(selectedStudent); // Fetch invoices for this student
        }
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(error.message || "Failed to fetch students");
    }
  };

  const handleStudentChange = async (selectedOption) => {
    formik.setFieldValue(
      "studentId",
      selectedOption ? selectedOption.value : ""
    );
    const studentId = selectedOption ? selectedOption.value : "";
    formik.setFieldValue("invoiceIds", []);
    formik.setFieldValue("paidAmount", "");
    setSelectedInvoice([]);
    setInvoiceData([]);
    setCreditCardFee(0); // Reset credit card fee
    if (studentId) {
      try {
        const response = await api.get(
          `/paymentInvoiceList?centerId=${selectedCenter}&studentId=${studentId}`
        );
        setInvoiceData(response.data);
      } catch (error) {
        toast.error(error.message || "Error fetching invoices");
      }
    }
  };

  const handleInvoiceChange = (selected) => {
    setSelectedInvoice(selected);
    const totalPaidAmount = selected.reduce(
      (sum, invoice) => sum + (invoice.totalAmount || 0),
      0
    );
    formik.setFieldValue(
      "invoiceIds",
      selected.map((option) => option.value)
    );
    formik.setFieldValue("paidAmount", totalPaidAmount.toFixed(2));
    // Recalculate credit card fee if payment method is Credit Card
    if (formik.values.paymentMethod === "Credit Card") {
      const fee = totalPaidAmount * 0.03; // 3% fee
      setCreditCardFee(fee);
    }
  };

  // Handle selectedCenter change: clear student selection and fetch new data
  useEffect(() => {
    // Clear the student selection
    formik.setFieldValue("studentId", student_Id || ""); // Preserve student_Id if present
    formik.setFieldValue("invoiceIds", []);
    formik.setFieldValue("paidAmount", "0");
    setCreditCardFee(0); // Reset credit card fee

    // Reset related states
    setStudentOptions([]);
    setStudentData(null);
    setInvoiceData([]);
    setSelectedInvoice([]);

    // Fetch new student data for the updated center
    fetchStudent(selectedCenter);
  }, [selectedCenter]);

  const handlePaymethodChange = (event) => {
    const paymentMethod = event.target.value;
    formik.setFieldValue("paymentMethod", paymentMethod);
    formik.setFieldValue("bank", "");
    formik.setFieldValue("transactionNo", "");
    formik.setFieldValue("mobileNumber", "");
    // Calculate credit card fee if payment method is Credit Card
    if (paymentMethod === "Credit Card") {
      const baseAmount = parseFloat(formik.values.paidAmount) || 0;
      const fee = baseAmount * 0.03; // 3% fee
      setCreditCardFee(fee);
    } else {
      setCreditCardFee(0); // Reset fee for other payment methods
    }
  };

  // Pre-select the invoice based on invoiceId from URL
  useEffect(() => {
    if (invoiceData.length > 0 && invoiceId) {
      const selectedInvoiceOption = invoiceOptions.find(
        (option) => option.value === parseInt(invoiceId)
      );
      if (selectedInvoiceOption) {
        const selectedInvoices = [selectedInvoiceOption]; // MultiSelect expects an array
        setSelectedInvoice(selectedInvoices);
        formik.setFieldValue(
          "invoiceIds",
          selectedInvoices.map((option) => option.value)
        );
        const totalAmount = selectedInvoices.reduce(
          (sum, invoice) => sum + (invoice.totalAmount || 0),
          0
        );
        formik.setFieldValue("paidAmount", totalAmount.toFixed(2));
        // Calculate credit card fee if payment method is Credit Card
        if (formik.values.paymentMethod === "Credit Card") {
          const fee = totalAmount * 0.03; // 3% fee
          setCreditCardFee(fee);
        }
      }
    }
  }, [invoiceData, invoiceId]);

  return (
    <div className="container-fluid">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/payments" className="custom-breadcrumb">
            Payment
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Payment Add
        </li>
      </ol>
      <form onSubmit={formik.handleSubmit}>
        <div className="card">
          <div
            className="d-flex justify-content-between align-items-center p-1 mb-4 px-4"
            style={{ background: "#f5f7f9" }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex">
                <div className="dot active"></div>
              </div>
              <span className="me-2 text-muted">Add Payment</span>
            </div>
            <div className="my-2 pe-3 d-flex align-items-center">
              <Link to="/payment">
                <button type="button" className="btn btn-sm btn-border">
                  Back
                </button>
              </Link>
                
              <button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                )}
                <span className="fw-medium">Save</span>
              </button>
            </div>
          </div>
          <div className="container-fluid px-4">
            <div className="row">
              <div className="col-md-6 col-12 mb-2">
                <label className="form-label">
                  Student Name<span className="text-danger">*</span>
                </label>
                <Select
                  options={studentOptions}
                  name="studentId"
                  value={
                    studentOptions.find(
                      (option) => option.value === formik.values.studentId
                    ) || null // fallback to null if not found
                  }
                  onChange={handleStudentChange}
                  placeholder="Select Student"
                  isSearchable
                  isClearable
                  className={`form-multi-select ${
                    formik.touched.studentId && formik.errors.studentId
                      ? "is-invalid"
                      : ""
                  }`}
                />
                {formik.touched.studentId && formik.errors.studentId && (
                  <div className="invalid-feedback">
                    {formik.errors.studentId}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2">
                <label className="form-label">
                  Invoice<span className="text-danger">*</span>
                </label>
                <MultiSelect
                  options={invoiceOptions}
                  value={selectedInvoice}
                  onChange={(selected) => handleInvoiceChange(selected)}
                  labelledBy="Select Invoice"
                  className={`form-multi-select ${
                    formik.touched.invoiceIds && formik.errors.invoiceIds
                      ? "is-invalid"
                      : ""
                  }`}
                />
                {formik.touched.invoiceIds && formik.errors.invoiceIds && (
                  <div className="invalid-feedback">
                    {formik.errors.invoiceIds}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Payment Date<span className="text-danger">*</span>
                  </label>
                  <input
                    name="paymentDate"
                    {...formik.getFieldProps("paymentDate")}
                    type="date"
                    className={`form-control  ${
                      formik.touched.paymentDate && formik.errors.paymentDate
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder=""
                  />
                  {formik.touched.paymentDate && formik.errors.paymentDate && (
                    <div className="invalid-feedback">
                      {formik.errors.paymentDate}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Payment Method<span className="text-danger">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("paymentMethod")}
                    name="paymentMethod"
                    className={`form-select  ${
                      formik.touched.paymentMethod &&
                      formik.errors.paymentMethod
                        ? "is-invalid"
                        : ""
                    }`}
                    onChange={handlePaymethodChange}
                  >
                    <option disabled value=""></option>
                    <option value="Cash">Cash</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Internet Banking">Internet Banking</option>
                    <option value="Paynow">Paynow</option>
                    <option value="Paynow QR">Paynow QR</option>
                  </select>
                  {formik.touched.paymentMethod &&
                    formik.errors.paymentMethod && (
                      <div className="invalid-feedback">
                        {formik.errors.paymentMethod}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Payment Reference</label>
                  <input
                    name="paymentReference"
                    {...formik.getFieldProps("paymentReference")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.paymentReference &&
                      formik.errors.paymentReference
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder=""
                  />
                </div>
              </div>
              {/* Conditionally Rendered Fields */}
              {(formik.values.paymentMethod === "Credit Card" ||
                formik.values.paymentMethod === "Internet Banking") && (
                <>
                  {/* Bank Name */}
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">
                      Bank<span className="text-danger">*</span>
                    </label>
                    <input
                      name="bank"
                      {...formik.getFieldProps("bank")}
                      type="text"
                      className={`form-control ${
                        formik.touched.bank && formik.errors.bank
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {formik.touched.bank && formik.errors.bank && (
                      <div className="invalid-feedback">
                        {formik.errors.bank}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Upload File</label>
                    <input
                      type="file"
                      name="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      className="form-control"
                      onChange={(event) => {
                        formik.setFieldValue("file", event.target.files[0]);
                      }}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </>
              )}
              {formik.values.paymentMethod === "Credit Card" && (
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Credit Card Charges (3%)
                  </label>
                  <input
                    name="accountNo"
                    value={creditCardFee.toFixed(2)}
                    type="text"
                    className="form-control"
                  />
                </div>
              )}
              {formik.values.paymentMethod === "Internet Banking" && (
                <>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Transaction No</label>
                    <input
                      name="transactionNo"
                      {...formik.getFieldProps("transactionNo")}
                      type="text"
                      className={`form-control ${
                        formik.touched.transactionNo &&
                        formik.errors.transactionNo
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {formik.touched.transactionNo &&
                      formik.errors.transactionNo && (
                        <div className="invalid-feedback">
                          {formik.errors.transactionNo}
                        </div>
                      )}
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Mobile Number</label>
                    <input
                      name="mobileNumber"
                      {...formik.getFieldProps("mobileNumber")}
                      type="text"
                      className={`form-control ${
                        formik.touched.mobileNumber &&
                        formik.errors.mobileNumber
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {formik.touched.mobileNumber &&
                      formik.errors.mobileNumber && (
                        <div className="invalid-feedback">
                          {formik.errors.mobileNumber}
                        </div>
                      )}
                  </div>
                </>
              )}

              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Paid Amount</label>
                  <input
                    name="paidAmount"
                    value={
                      formik.values.paymentMethod === "Credit Card"
                        ? (
                            parseFloat(formik.values.paidAmount || 0) +
                            creditCardFee
                          ).toFixed(2)
                        : formik.values.paidAmount
                    }
                    type="text"
                    className={`form-control  ${
                      formik.touched.paidAmount && formik.errors.paidAmount
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.paidAmount && formik.errors.paidAmount && (
                    <div className="invalid-feedback">
                      {formik.errors.paidAmount}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <label className="form-label">Remark</label>
                <div className="input-group mb-3">
                  <textarea
                    name="remark"
                    className="form-control"
                    {...formik.getFieldProps("remark")}
                    id="remark"
                    rows="5"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default PaymentsAdd;
